// ---------------------------------------------------------------
// authService.js — Utilidades de autenticación compartidas.
// Permite vincular contraseña a cuentas Google y resolver
// correo/username para el login.
// ---------------------------------------------------------------
import {
  EmailAuthProvider,
  linkWithCredential,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updatePassword,
} from "firebase/auth";
import { auth } from "../firebase";
import { resolveUsername } from "./userService";

export function hasPasswordProvider(user) {
  return user?.providerData?.some((p) => p.providerId === "password") ?? false;
}

/** Convierte correo o username en el email de acceso de Firebase Auth. */
export async function resolveIdentifierToEmail(identifier) {
  const raw = (identifier || "").trim();
  if (!raw) return null;
  if (raw.includes("@")) return raw.toLowerCase();
  return resolveUsername(raw);
}

/**
 * Envía un correo para establecer o restablecer la contraseña.
 * En cuentas creadas con Google, este es el paso que habilita el
 * inicio de sesión con correo/username + contraseña.
 */
export async function sendPasswordSetupEmail(identifier) {
  const email = await resolveIdentifierToEmail(identifier);
  if (!email) {
    throw Object.assign(new Error(), { code: "app/username-not-found" });
  }
  await sendPasswordResetEmail(auth, email);
  return email;
}

/** Vincula contraseña a la sesión actual (p. ej. tras entrar con Google). */
export async function linkPasswordToAccount(user, password) {
  if (!user?.email) {
    throw Object.assign(new Error(), { code: "app/missing-email" });
  }
  const credential = EmailAuthProvider.credential(user.email, password);
  await linkWithCredential(user, credential);
}

/**
 * Cambia la contraseña del usuario actual. Firebase exige haber
 * iniciado sesión hace poco, así que primero se vuelve a validar
 * con la contraseña actual.
 */
export async function changePassword(user, currentPassword, newPassword) {
  if (!user?.email) {
    throw Object.assign(new Error(), { code: "app/missing-email" });
  }
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
}

/**
 * Cierra la sesión Y vacía la copia local de los datos.
 *
 * Firestore guarda en el móvil una copia de todo lo que la web ha leído
 * (fichajes, turnos del equipo, tareas…) para ir más rápido y funcionar
 * con mala cobertura. `signOut` sólo cierra la sesión: esa copia se
 * quedaba en el dispositivo hasta que alguien borrase los datos del
 * navegador. Ahora se borra al salir.
 *
 * Lo que NO se borra a propósito: el código del dispositivo vinculado
 * para fichar (`fichaje-device-id`). Si se borrara, la persona tendría
 * que pedir a la dirección que le desvinculara el móvil para volver a
 * fichar desde él.
 */
export async function cerrarSesion() {
  const { signOut } = await import("firebase/auth");
  const { terminate, clearIndexedDbPersistence } = await import("firebase/firestore");
  const { db } = await import("../firebase");
  try {
    await signOut(auth);
  } catch (err) {
    console.error("No se pudo cerrar la sesión:", err);
  }
  try {
    await terminate(db);
    await clearIndexedDbPersistence(db);
  } catch (err) {
    // Con la web abierta en otra pestaña la copia está en uso y no se
    // puede borrar; se borrará al cerrar sesión desde la última.
    console.warn("No se pudo vaciar la copia local:", err);
  }
  // Firestore queda inservible tras `terminate`: se recarga la página
  window.location.reload();
}
